import { useEffect, useId, useRef, useState, type RefObject } from 'react';
import {
  fetchBrowse,
  fetchSearch,
  type BrowseRow,
  type SearchResponse,
  type SearchResultRow,
  type SearchTier,
} from '../lib/api';
import { DocId } from '../lib/doc-id';
import { InfoTip } from '../lib/info-tip';
import { formatShortDate } from '../lib/format-date';
import { browseOutcome, browseOutcomeKey, documentTypeLabel, documentTypeOrder, failureClassHeading, labels, type BrowseOutcomeKey } from '../lib/labels';
import { LoadError } from '../lib/load-error';
import { readHash } from '../lib/use-hash-route';
import { Pager, usePagedList } from '../lib/use-paged-list';

const TIERS: readonly SearchTier[] = ['7', '30', '90', 'all'];

type Phase = 'idle' | 'searching' | 'done' | 'stopped' | 'error';

interface SearchState {
  phase: Phase;
  tier: SearchTier;
  response: SearchResponse | null;
}

const IDLE_STATE: SearchState = { phase: 'idle', tier: '7', response: null };

/**
 * `#documents`: the search screen over the index and the doc-id
 * short-circuit. The header's search field (`AppShell.tsx`) is the only input; this component
 * only ever *reads* the current query off the route (`query`, from `#documents?q=...`) and drives
 * the self-widening search that query implies, one `?tier=` request at a time — **7 → 30 → 90 →
 * all**, narrated, with a **Stop**, "surfacing results as they arrive".
 * It stops widening at the first tier with matches. Each tier's response is complete,
 * not a delta, so replacing the displayed response on every tier is correct and needs no merge step.
 *
 * Index-First (`design.md` § Macrostructure families): the results table is the page, and the
 * narration sits *under* it rather than above, so a result that arrives on tier 7 does not get
 * pushed down the screen when tier 30 starts.
 */
export function Documents({ query }: { query: string }) {
  const [state, setState] = useState<SearchState>(IDLE_STATE);
  // One AbortController per query, covering the whole widening loop: Stop, the query changing,
  // and unmount all just abort it. `signal.aborted` is the single "stop looping" check — no
  // separate boolean needed to track the same fact the controller already carries.
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    controllerRef.current = controller;

    if (query.trim().length === 0) {
      setState(IDLE_STATE);
      return undefined;
    }

    async function runSearch() {
      // A new query drops the old response before the first tier starts, so the old rows and
      // narration do not stay on the screen.
      setState({ phase: 'searching', tier: TIERS[0], response: null });
      for (const tier of TIERS) {
        if (controller.signal.aborted) return;
        setState((prev) => ({ ...prev, phase: 'searching', tier }));

        let response: SearchResponse;
        try {
          response = await fetchSearch(query, tier, controller.signal);
        } catch {
          if (controller.signal.aborted) return;
          setState((prev) => ({ ...prev, phase: 'error' }));
          return;
        }
        if (controller.signal.aborted) return;

        // A doc id never widens (it resolved or it did not, regardless of tier); a join-key
        // response widens automatically unless this was already the last tier or it has matches
        // (ADR 0034: a wider tier would only add older documents the operator did not wait for).
        const finished = tier === 'all' || response.mode !== 'join-key' || response.results.length > 0;
        setState({ phase: finished ? 'done' : 'searching', tier, response });
        if (finished) return;
      }
    }

    void runSearch();

    return () => controller.abort();
  }, [query]);

  function handleStop() {
    controllerRef.current?.abort();
    setState((prev) => ({ ...prev, phase: 'stopped' }));
  }

  return (
    <div>
      <div className="page-head">
        <h2>{labels.pageTitles.documents}</h2>
        {query.trim().length === 0 && <p className="page-head__sub">{labels.documents.browse.sub}</p>}
      </div>
      {query.trim().length === 0 ? <BrowseList /> : <SearchBody state={state} onStop={handleStop} />}
    </div>
  );
}

/**
 * QA-68: with no search, the page lists every document, newest activity first. A page is 20 rows.
 * QA-69: the paging is the one of **Test extraction** (`usePagedList`). The list does not poll: a
 * new document shows when the operator opens the page again.
 *
 * QA-115: the list is a table with a filter bar (status, type, a date range, **Clear filters**) and
 * sortable column heads. The browse endpoint has no filter, so the filters and the sort work on the
 * page on the screen, and a scope line says so. They live in the hash query
 * (`#documents?status=review&sort=type`), so opening a document and pressing Back shows the same
 * filtered list. A search (`?q=`) never carries them.
 *
 * Why this screen keeps its own query keys next to `lib/use-hash-route.ts`: the router owns the
 * route (`#documents`) and `?q=`, which pick the screen. The filter keys only change what this list
 * shows, so the list writes them itself with `replaceState`, which does not fire `hashchange`: a
 * filter change does not re-render the route or add a history entry for each click. Any other
 * hash change (the rail's Documents link, Back, an edited URL) does fire it, and the list reads its
 * view from the hash again, so the screen always matches the URL (review findings 3 and 6).
 */
type SortKey = 'at' | 'type' | 'status';
type SortDir = 'asc' | 'desc';
type OutcomeKey = BrowseOutcomeKey;

interface BrowseView {
  status: OutcomeKey | '';
  type: string;
  from: string;
  to: string;
  /** `''` until the operator sorts: the server's order (newest activity first), with no arrow. */
  sort: SortKey | '';
  dir: SortDir;
}

const DEFAULT_VIEW: BrowseView = { status: '', type: '', from: '', to: '', sort: '', dir: 'desc' };
const OUTCOME_KEYS: readonly OutcomeKey[] = ['accepted', 'review', 'reviewed'];
const SORT_KEYS: readonly SortKey[] = ['at', 'type', 'status'];
const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** Reads the view from the hash query. A value it does not know falls back to the default. */
function readView(): BrowseView {
  const params = readHash().query;
  const pick = <T extends string>(key: string, allowed: readonly T[], fallback: T): T => {
    const value = params.get(key) as T | null;
    return value !== null && allowed.includes(value) ? value : fallback;
  };
  const day = (key: string) => {
    const value = params.get(key) ?? '';
    return DAY.test(value) ? value : '';
  };
  return {
    status: pick<OutcomeKey | ''>('status', OUTCOME_KEYS, ''),
    type: pick<string>('type', documentTypeOrder, ''),
    from: day('from'),
    to: day('to'),
    sort: pick<SortKey | ''>('sort', SORT_KEYS, ''),
    dir: pick<SortDir>('dir', ['asc', 'desc'], 'desc'),
  };
}

/** `replaceState`, not `location.hash`: no `hashchange`, so the route does not re-render, and Back
 *  from a document returns to this entry with the view in it. */
function writeView(view: BrowseView): void {
  const params = new URLSearchParams();
  for (const key of Object.keys(DEFAULT_VIEW) as (keyof BrowseView)[]) {
    if (view[key] !== DEFAULT_VIEW[key]) params.set(key, view[key]);
  }
  const query = params.toString();
  window.history.replaceState(window.history.state, '', `#documents${query ? `?${query}` : ''}`);
}

/** The local calendar day of an ISO time, the day the row's time shows. */
function localDay(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function applyView(rows: BrowseRow[], view: BrowseView): BrowseRow[] {
  const shown = rows.filter((row) => {
    if (view.status && browseOutcomeKey(row) !== view.status) return false;
    if (view.type && row.type !== view.type) return false;
    const day = localDay(row.at);
    if (view.from && day < view.from) return false;
    if (view.to && day > view.to) return false;
    return true;
  });
  if (view.sort === '') return shown;
  const sort = view.sort;
  const sign = view.dir === 'asc' ? 1 : -1;
  const byTime = (a: BrowseRow, b: BrowseRow) => a.at.localeCompare(b.at);
  const compare: Record<SortKey, (a: BrowseRow, b: BrowseRow) => number> = {
    at: byTime,
    type: (a, b) => documentTypeLabel(a.type).localeCompare(documentTypeLabel(b.type)),
    status: (a, b) => STATUS_ORDER.indexOf(browseOutcomeKey(a)) - STATUS_ORDER.indexOf(browseOutcomeKey(b)),
  };
  // A tie keeps the newest first, whichever way the column sorts.
  return [...shown].sort((a, b) => sign * compare[sort](a, b) || -byTime(a, b));
}

/** QA-130: the status sort follows this order, not the label text, so a label change never reorders. */
const STATUS_ORDER: readonly BrowseOutcomeKey[] = ['accepted', 'review', 'reviewed'];

function BrowseList() {
  const { browse } = labels.documents;
  const listRef = useRef<HTMLElement>(null);
  const statusRef = useRef<HTMLSelectElement>(null);
  const [view, setView] = useState<BrowseView>(readView);
  // A hash change this list did not write (the rail's Documents link, Back, an edited URL) sets the
  // view the URL now names, so the screen and the URL agree.
  useEffect(() => {
    const onHashChange = () => {
      // Leaving for a document unmounts the list; keep the view it had until then.
      if (readHash().path === 'documents') setView(readView());
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);
  const list = usePagedList<BrowseRow>(
    (cursor, signal) => fetchBrowse(cursor, signal).then(({ rows, nextCursor }) => ({ items: rows, nextCursor })),
    0,
    false,
    listRef,
  );
  const { page, failed } = list;

  function update(change: Partial<BrowseView>) {
    const next = { ...view, ...change };
    setView(next);
    writeView(next);
  }

  function clearFilters() {
    update({ status: '', type: '', from: '', to: '' });
    statusRef.current?.focus();
  }

  function sortBy(key: SortKey) {
    if (view.sort === key) update({ dir: view.dir === 'asc' ? 'desc' : 'asc' });
    else update({ sort: key, dir: key === 'at' ? 'desc' : 'asc' });
  }

  const filtered = view.status !== '' || view.type !== '' || view.from !== '' || view.to !== '';
  const rows = page ? applyView(page.items, view) : [];

  return (
    <section ref={listRef} tabIndex={-1} aria-label={browse.listLabel}>
      <BrowseFilters view={view} statusRef={statusRef} filtered={filtered} onChange={update} onClear={clearFilters} />
      {failed && !page && <LoadError text={labels.documents.loadFailed} onRetry={list.retry} />}
      {!failed && !page && <p className="notice">{browse.loading}</p>}
      {page && page.items.length === 0 && <p className="notice">{list.first ? browse.empty : labels.paging.noOlder}</p>}
      {page && page.items.length > 0 && (
        <>
          <p className="browse-scope" aria-live="polite">
            {browse.filters.scope(rows.length, page.items.length)}
          </p>
          {rows.length === 0 ? (
            <p className="notice">{browse.filters.noMatch}</p>
          ) : (
            <BrowseTable rows={rows} view={view} onSort={sortBy} />
          )}
        </>
      )}
      <Pager list={list} focusTarget={listRef} />
    </section>
  );
}

function BrowseFilters({
  view,
  statusRef,
  filtered,
  onChange,
  onClear,
}: {
  view: BrowseView;
  statusRef: RefObject<HTMLSelectElement>;
  filtered: boolean;
  onChange: (change: Partial<BrowseView>) => void;
  onClear: () => void;
}) {
  const { filters, outcomes } = labels.documents.browse;
  const id = useId();
  return (
    <div className="browse-filters" role="group" aria-label={filters.label}>
      <div className="browse-filters__field">
        <label htmlFor={`${id}-status`}>{filters.status}</label>
        <select
          id={`${id}-status`}
          ref={statusRef}
          value={view.status}
          onChange={(event) => onChange({ status: event.target.value as OutcomeKey | '' })}
        >
          <option value="">{filters.any}</option>
          {OUTCOME_KEYS.map((key) => (
            <option key={key} value={key}>
              {outcomes[key]}
            </option>
          ))}
        </select>
      </div>
      <div className="browse-filters__field">
        <label htmlFor={`${id}-type`}>{filters.type}</label>
        <select id={`${id}-type`} value={view.type} onChange={(event) => onChange({ type: event.target.value })}>
          <option value="">{filters.any}</option>
          {documentTypeOrder.map((type) => (
            <option key={type} value={type}>
              {documentTypeLabel(type)}
            </option>
          ))}
        </select>
      </div>
      <div className="browse-filters__field">
        <label htmlFor={`${id}-from`}>{filters.from}</label>
        <input
          id={`${id}-from`}
          type="date"
          value={view.from}
          max={view.to || undefined}
          onChange={(event) => onChange({ from: event.target.value })}
        />
      </div>
      <div className="browse-filters__field">
        <label htmlFor={`${id}-to`}>{filters.to}</label>
        <input
          id={`${id}-to`}
          type="date"
          value={view.to}
          min={view.from || undefined}
          onChange={(event) => onChange({ to: event.target.value })}
        />
      </div>
      {filtered && (
        <button type="button" className="btn btn--text" onClick={onClear}>
          {filters.clear}
        </button>
      )}
    </div>
  );
}

/** The pill tone of each outcome, keyed by `browseOutcomeKey` so every outcome has one. */
const TONE: Record<OutcomeKey, string> = { accepted: 'ok', review: 'warn', reviewed: 'info' };

/** A sortable column head. Only the sorted column carries `aria-sort`. */
function SortHead({ sortKey, view, onSort }: { sortKey: SortKey; view: BrowseView; onSort: (key: SortKey) => void }) {
  const { columns, sortMark, needsReviewTip } = labels.documents.browse;
  const active = view.sort === sortKey;
  const direction = view.dir === 'asc' ? 'ascending' : 'descending';
  return (
    <th aria-sort={active ? direction : undefined}>
      <button type="button" className="sort-head" onClick={() => onSort(sortKey)}>
        {columns[sortKey]}
        {active && (
          <span className="sort-head__arrow" aria-hidden="true">
            {sortMark[direction]}
          </span>
        )}
      </button>
      {/* A sibling of the sort button: the ⓘ never sits inside another button (QA-126). */}
      {sortKey === 'status' && <InfoTip label={columns.status} text={needsReviewTip} />}
    </th>
  );
}

function BrowseTable({ rows, view, onSort }: { rows: BrowseRow[]; view: BrowseView; onSort: (key: SortKey) => void }) {
  const { columns, joinKey } = labels.documents.browse;
  const keyColumns = labels.documents.columns;

  return (
    <div className="responsive-table browse-table-wrap">
      <table className="browse-table">
        <thead>
          <tr>
            <th>{columns.document}</th>
            <SortHead sortKey="type" view={view} onSort={onSort} />
            <SortHead sortKey="at" view={view} onSort={onSort} />
            <SortHead sortKey="status" view={view} onSort={onSort} />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const keys = [
              row.pedimento && joinKey(keyColumns.pedimento, row.pedimento),
              row.cove && joinKey(keyColumns.cove, row.cove),
              row.invoice && joinKey(keyColumns.invoice, row.invoice),
            ].filter(Boolean);
            return (
              <tr key={row.docId}>
                <td>
                  <a className="mono" href={`#doc/${row.docId}`} aria-label={row.docId} title={row.docId}>
                    <DocId id={row.docId} />
                  </a>
                  {keys.length > 0 && <span className="browse-table__keys">{keys.join(' · ')}</span>}
                </td>
                <td>{documentTypeLabel(row.type)}</td>
                <td className="mono">
                  <time dateTime={row.at}>{formatShortDate(row.at)}</time>
                </td>
                <td>
                  {/* The same status pill as the document cards (QA-93): Accepted green, Sent to
                      review amber, Reviewed neutral. */}
                  <span className="doc-card__status" data-tone={TONE[browseOutcomeKey(row)]}>
                    {browseOutcome(row)}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SearchBody({ state, onStop }: { state: SearchState; onStop: () => void }) {
  if (state.phase === 'error')
    return (
      <p className="notice notice--crit" role="alert">
        {labels.documents.loadFailed}
      </p>
    );

  // Before the first tier's response lands, `state.response` is still `null` and the doc-id vs.
  // join-key branch below cannot run yet — but that first fetch is already in flight and just as
  // abortable as any later one, so Stop has to be offered here too, not only from the second
  // fetch onward.
  if (!state.response) return <InitialNarration state={state} onStop={onStop} />;

  if (state.response.mode === 'empty') return null;
  if (state.response.mode === 'doc-id') return <DocIdResult response={state.response} />;
  return <JoinKeyResult state={state} response={state.response} onStop={onStop} />;
}

function InitialNarration({ state, onStop }: { state: SearchState; onStop: () => void }) {
  const days = state.tier === 'all' ? null : Number(state.tier);

  if (state.phase === 'stopped') {
    return <p className="notice">{days === null ? labels.documents.searchingAll : labels.documents.stopped(days)}</p>;
  }

  return (
    <p className="notice">
      {days === null ? labels.documents.searchingAll : labels.documents.searching(days)}{' '}
      <button type="button" className="btn btn--text" onClick={onStop}>
        {labels.documents.stop}
      </button>
    </p>
  );
}

function DocIdResult({ response }: { response: Extract<SearchResponse, { mode: 'doc-id' }> }) {
  if (response.found) {
    return (
      <p className="notice notice--good">
        {labels.documents.foundHeadline}{' '}
        <a href={`#doc/${response.docId}`} aria-label={`${response.docId} — ${labels.documents.viewDocument}`}>
          <span className="mono">
            <DocId id={response.docId} />
          </span>{' '}
          — {labels.documents.viewDocument}
        </a>
      </p>
    );
  }

  if (response.notProcessed) {
    const { failureClass, ignored } = response.notProcessed;
    const group = failureClassHeading(failureClass);
    return (
      <p className="notice">
        {ignored ? labels.documents.onNotProcessedIgnored(group) : labels.documents.onNotProcessed(group)}{' '}
        <a href={`#doc/${response.docId}`} aria-label={`${response.docId} — ${labels.documents.viewDocument}`}>
          {labels.documents.viewDocument}
        </a>
      </p>
    );
  }

  return (
    <p className="notice">
      {labels.documents.notFoundHeadline}{' '}
      {response.inFlight ? response.inFlight.message : labels.documents.notFoundFallback}
    </p>
  );
}

function JoinKeyResult({
  state,
  response,
  onStop,
}: {
  state: SearchState;
  response: Extract<SearchResponse, { mode: 'join-key' }>;
  onStop: () => void;
}) {
  return (
    <div>
      {response.results.length > 0 && <ResultsTable query={response.query} rows={response.results} />}
      <Narration state={state} days={response.daysSearched} resultCount={response.results.length} onStop={onStop} />
    </div>
  );
}

function Narration({
  state,
  days,
  resultCount,
  onStop,
}: {
  state: SearchState;
  days: number | null;
  resultCount: number;
  onStop: () => void;
}) {
  if (state.phase === 'searching') {
    return (
      <p className="notice">
        {days === null ? labels.documents.searchingAll : labels.documents.stillLooking(days)}{' '}
        <button type="button" className="btn btn--text" onClick={onStop}>
          {labels.documents.stop}
        </button>
      </p>
    );
  }

  if (state.phase === 'stopped') {
    return <p className="notice">{days === null ? labels.documents.searchingAll : labels.documents.stopped(days)}</p>;
  }

  if (resultCount === 0) {
    return (
      <p className="notice">{days === null ? labels.documents.noResultsAll : labels.documents.noResultsYet(days)}</p>
    );
  }

  return null;
}

function ResultsTable({ query, rows }: { query: string; rows: SearchResultRow[] }) {
  return (
    <section aria-labelledby="search-results-heading">
      <div className="panel__head">
        <h2 id="search-results-heading">{labels.documents.resultsHeading(query)}</h2>
      </div>
      <div className="responsive-table">
        <table>
          <caption>{labels.documents.resultCount(rows.length)}</caption>
          <thead>
            <tr>
              <th>{labels.documents.columns.document}</th>
              <th>{labels.documents.columns.pedimento}</th>
              <th>{labels.documents.columns.cove}</th>
              <th>{labels.documents.columns.invoice}</th>
              <th>{labels.documents.columns.caseKey}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.docId}>
                <td>
                  <a href={`#doc/${row.docId}`} aria-label={`${documentTypeLabel(row.type)} · ${row.docId}`}>
                    {documentTypeLabel(row.type)} ·{' '}
                    <span className="mono">
                      <DocId id={row.docId} />
                    </span>
                  </a>
                </td>
                {/* Join keys are pipeline values the operator compares character by character, so
                    they are mono and tabular — a proportional 1 next to a proportional 7 in a
                    column of pedimento numbers is how a mis-read happens. */}
                <td className="mono">{row.pedimento ?? '—'}</td>
                <td className="mono">{row.cove ?? '—'}</td>
                <td className="mono">{row.invoice ?? '—'}</td>
                <td className="mono">{row.caseKey ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
